import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link } from 'react-router-dom';
import { MailCheck } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useSolicitarRecuperacaoSenha } from '@/services/Auth';
import {
  RecuperarSenhaFormData,
  recuperarSenhaValidationSchema,
} from '../validators/recuperarSenhaValidator';

export function RecuperarSenha() {
  const [enviado, setEnviado] = useState(false);
  const { mutate, isPending } = useSolicitarRecuperacaoSenha();

  const { handleSubmit, control, getValues, formState } = useForm<RecuperarSenhaFormData>({
    resolver: zodResolver(recuperarSenhaValidationSchema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  });
  const { errors } = formState;

  function solicitar(dados: RecuperarSenhaFormData) {
    // A resposta do backend é SEMPRE a mesma, exista ou não o e-mail -- não é
    // oráculo de cadastro. Por isso a tela sempre avança para a confirmação.
    mutate({ Email: dados.email }, { onSuccess: () => setEnviado(true) });
  }

  return (
    <main className="relative min-h-screen w-full overflow-hidden bg-linear-to-br from-[#9ebe3b] via-[#7da32e] to-[#6b8e28] grid grid-cols-1 lg:grid-cols-2">
      <div className="hidden lg:flex flex-col items-center justify-center p-16 relative z-10">
        <div className="animate-fade-in">
          <h1 className="text-5xl xl:text-6xl font-extrabold text-white mb-4 leading-tight drop-shadow-lg">
            Esqueceu sua senha?
          </h1>
          <p className="text-lg text-white/90 leading-relaxed font-normal">
            Sem problema. Vamos te ajudar a criar uma nova.
          </p>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center p-8 relative z-20 bg-white">
        <img
          src="/logo-it-works.png"
          alt="IT Works Logo"
          className="w-[270px] h-auto mb-8 animate-fade-in"
        />

        <div className="bg-white rounded-3xl p-8 sm:p-12 w-full max-w-[460px] shadow-[0_20px_60px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)] animate-fade-in">
          {enviado ? (
            <div className="flex flex-col items-center gap-4 text-center">
              <MailCheck className="h-10 w-10 text-[#7da32e]" />
              <h2 className="text-xl font-bold text-[#2d3748]">Verifique seu e-mail</h2>
              <p className="text-sm text-zinc-500">
                Se <strong>{getValues('email')}</strong> estiver cadastrado, você vai receber um
                link para redefinir a senha em alguns minutos.
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => mutate({ Email: getValues('email') })}
                disabled={isPending}
              >
                {isPending ? 'Reenviando...' : 'Reenviar e-mail'}
              </Button>
              <Link to="/login" className="text-sm text-[#7da32e] hover:underline">
                Voltar para o login
              </Link>
            </div>
          ) : (
            <>
              <div className="mb-8 text-center">
                <h2 className="text-2xl font-bold bg-linear-to-br from-[#2d3748] to-[#1a202c] bg-clip-text text-transparent mb-2">
                  Esqueci minha senha
                </h2>
                <p className="text-sm text-zinc-500">
                  Informe o e-mail da sua conta para receber o link de redefinição.
                </p>
              </div>

              <form onSubmit={handleSubmit(solicitar)} className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <label htmlFor="email" className="text-sm font-semibold text-[#4a5568]">
                    E-mail
                  </label>
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        autoFocus
                        {...field}
                      />
                    )}
                  />
                  {errors.email?.message && (
                    <span className="text-[#e53e3e] text-xs font-medium">
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <Button type="submit" className="w-full h-12" disabled={isPending}>
                  {isPending ? 'Enviando...' : 'Enviar link de redefinição'}
                </Button>

                <Link to="/login" className="text-sm text-center text-[#7da32e] hover:underline">
                  Voltar para o login
                </Link>
              </form>
            </>
          )}
        </div>
      </div>
    </main>
  );
}
